/**
 * lx-toast —— 底部轻提示（全局层）
 *
 * 挂在 body 上，不随页面切换消失。`ui.toast('...')` 指令通过
 * `ui:toast` 事件触发它。
 *
 * 为什么不跟模态框合并：模态框要用户点一下才走，toast 是自己走的。
 * 两者的生命周期完全不同，混在一起会让「哪些提示需要确认」变得含混。
 */

import { LxElement, define } from '@/core/base-element';

interface ToastItem {
  id: number;
  text: string;
  sticky: boolean;
  leaving: boolean;
}

let seq = 0;

export class LxToast extends LxElement {
  private _items: ToastItem[] = [];

  protected styles(): string {
    return `
      :host {
        position: fixed;
        left: 0; right: 0;
        bottom: calc(72px + env(safe-area-inset-bottom, 0px));
        z-index: var(--z-toast, 60);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--sp-2);
        pointer-events: none;
        padding: 0 var(--sp-4);
      }

      .toast {
        max-width: min(90%, 360px);
        padding: var(--sp-2) var(--sp-4);
        border-radius: var(--radius-full);
        background: var(--c-fg);
        color: var(--c-bg);
        font-size: var(--fs-sm);
        line-height: var(--lh-base);
        box-shadow: var(--shadow-md, 0 4px 16px rgba(0,0,0,.2));
        animation: rise var(--dur-base) var(--ease-standard);
        text-align: center;
      }

      .toast.leaving {
        animation: fall var(--dur-base) var(--ease-standard) forwards;
      }

      @keyframes rise {
        from { transform: translateY(8px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
      @keyframes fall {
        to { transform: translateY(8px); opacity: 0; }
      }
    `;
  }

  protected render(): string {
    return this._items
      .map(
        (t) =>
          `<div class="toast${t.leaving ? ' leaving' : ''}" data-id="${t.id}">${this.esc(t.text)}</div>`,
      )
      .join('');
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.subscribe('ui:toast', ({ text, sticky }) => {
      // 空文本 + sticky 是一个约定的「清除」信号：
      // 常驻提示（ui.loading）需要一种方式收尾，否则它会永远挂在屏幕上。
      // 用同一个事件类型而不是新造一个 ui:toast-clear —— 语义上
      // 「让提示区变成这个样子」比「执行一次清除动作」更贴合这里的模型。
      if (sticky && !text) {
        this._clearSticky();
        return;
      }

      const item: ToastItem = { id: ++seq, text, sticky: Boolean(sticky), leaving: false };
      this._items.push(item);
      // 最多同时显示 3 条
      if (this._items.length > 3) this._items.shift();
      this.update();

      if (!item.sticky) {
        setTimeout(() => this.dismiss(item.id), 2600);
      }
    });
  }

  /** 移除所有常驻提示（带动画），保留普通提示 */
  private _clearSticky(): void {
    const stickyIds = this._items.filter((t) => t.sticky && !t.leaving).map((t) => t.id);
    if (stickyIds.length === 0) return;
    stickyIds.forEach((id) => {
      const it = this._items.find((t) => t.id === id);
      if (it) it.leaving = true;
    });
    this.update();
    setTimeout(() => {
      this._items = this._items.filter((t) => !(t.sticky && t.leaving));
      this.update();
    }, 220);
  }

  private dismiss(id: number): void {
    const it = this._items.find((t) => t.id === id);
    if (!it) return;
    it.leaving = true;
    this.update();
    setTimeout(() => {
      this._items = this._items.filter((t) => t.id !== id);
      this.update();
    }, 220);
  }
}

define('lx-toast', LxToast);
